<template>
  <div class="min-h-screen bg-[#F8FAFC] p-4 md:p-6 lg:p-8 font-sans text-slate-900 space-y-6 selection:bg-green-100">

    <!-- EN-TÊTE PRINCIPAL ET SÉLECTEUR D'AGENCE -->
    <header class="flex  lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative">
      <div class="flex items-center gap-4 w-full lg:w-auto">
        <div class="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center text-white shadow-md shadow-emerald-500/20 shrink-0 text-lg">
          📊
        </div>
        
        <div class="flex-1 lg:flex-none">
          <div class="flex items-center gap-2 text-xs font-semibold text-slate-400">
            <span>Medialink Solutions</span>
            <span>/</span>
            <span class="text-emerald-600">Rapports Pocket Care</span>
          </div>

          <!-- SÉLECTEUR AVANCÉ RECHERCHABLE (+1000 AGENCES) -->
          <div class="relative mt-1 w-full lg:w-80">
            <div 
              @click="isAgencyDropdownOpen = !isAgencyDropdownOpen"
              class="bg-slate-50 border border-slate-200 hover:border-emerald-300 text-slate-900 font-bold px-3.5 py-2 rounded-xl text-xs flex items-center justify-between cursor-pointer transition-all"
            >
              <span class="truncate">{{ selectedAgencyName || '📊 Vue globale (toutes les agences)' }}</span>
              <svg class="w-4 h-4 text-slate-400 shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            <!-- Dropdown Panel -->
            <div v-if="isAgencyDropdownOpen" class="absolute left-0 right-0 mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 p-2 space-y-2 animate-scale-in">
              <input 
                type="text" 
                v-model="agencySearchQuery" 
                placeholder="Filtrer parmi les agences..." 
                class="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:border-emerald-500"
                autofocus
              >
              <div class="max-h-60 overflow-y-auto space-y-1 custom-scrollbar">
                <div 
                  @click="selectAgency('')"
                  class="px-3 py-2 rounded-xl text-xs font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 cursor-pointer transition-colors truncate"
                  :class="{ 'bg-emerald-50 text-emerald-600': selectedAgencyId === '' }"
                >
                  📊 Vue globale (toutes les agences)
                </div>
                <div 
                  v-for="agency in filteredAgencies" 
                  :key="agency.id"
                  @click="selectAgency(agency.id)"
                  class="px-3 py-2 rounded-xl text-xs font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 cursor-pointer transition-colors truncate"
                  :class="{ 'bg-emerald-50 text-emerald-600': selectedAgencyId === agency.id }"
                >
                  💊 {{ agency.nom }}
                </div>
                <div v-if="filteredAgencies.length === 0" class="p-4 text-center text-xs text-slate-400">
                  Aucune pharmacie trouvée
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- PÉRIODE ET BOUTON CHARGER -->
      <div class="flex flex-wrap items-center gap-3 w-full lg:w-auto justify-end">
        <div class="w-full sm:w-auto">
          <input
            v-model="selectedMonth"
            type="month"
            class="w-full sm:w-auto px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:border-emerald-500 transition-all"
          />
        </div>
        <button
          @click="loadData"
          class="w-full sm:w-auto flex items-center justify-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-xl font-bold text-xs hover:bg-emerald-600 shadow-sm transition-all"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-width="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          Charger
        </button>

        <!-- Boutons d'export (visibles si données chargées) -->
        <div v-if="data" class="flex items-center gap-2 border-l border-slate-200 pl-3">
          <button
            @click="exportExcel"
            class="flex items-center gap-1.5 px-4 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-xl font-bold text-xs transition-all shadow-sm active:scale-95"
            title="Exporter en Excel"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-width="2.5" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Excel
          </button>
          <button
            @click="exportCSV"
            class="flex items-center gap-1.5 px-4 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-xl font-bold text-xs transition-all shadow-sm active:scale-95"
            title="Exporter en CSV"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-width="2.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            CSV
          </button>
        </div>
      </div>
    </header>

    <!-- KPI GRID -->
    <div v-if="data" class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div class="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
        <div>
          <p class="text-[10px] font-bold uppercase text-slate-400 tracking-wider">Total Tickets</p>
          <p class="text-2xl font-black mt-1 tabular-nums text-slate-900">{{ data.totalChoices }}</p>
        </div>
        <div class="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-xl border border-blue-100">🎫</div>
      </div>

      <div class="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
        <div>
          <p class="text-[10px] font-bold uppercase text-slate-400 tracking-wider">Acceptations</p>
          <p class="text-2xl font-black mt-1 tabular-nums text-green-600">{{ data.acceptedCount }}</p>
        </div>
        <div class="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center text-xl border border-green-100">✅</div>
      </div>

      <div class="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
        <div>
          <p class="text-[10px] font-bold uppercase text-slate-400 tracking-wider">Refus</p>
          <p class="text-2xl font-black mt-1 tabular-nums text-red-600">{{ data.refusedCount }}</p>
        </div>
        <div class="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center text-xl border border-red-100">❌</div>
      </div>

      <div class="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
        <div>
          <p class="text-[10px] font-bold uppercase text-slate-400 tracking-wider">Taux Acceptation</p>
          <p class="text-2xl font-black mt-1 tabular-nums text-indigo-600">{{ data.acceptanceRate }}%</p>
        </div>
        <div class="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-xl border border-indigo-100">📈</div>
      </div>
    </div>

    <!-- STATISTIQUES PAR FILE -->
    <div v-if="data && Object.keys(data.byQueue).length > 0" class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
      <h2 class="text-xs font-bold uppercase tracking-wider text-slate-400">Statistiques par File d'Attente</h2>
      <div class="grid md:grid-cols-2 gap-4">
        <div
          v-for="(stats, queueName) in data.byQueue"
          :key="queueName"
          class="p-4 bg-slate-50 rounded-xl border border-slate-200/60"
        >
          <div class="flex items-center justify-between mb-2">
            <h3 class="font-bold text-xs text-slate-800">{{ queueName }}</h3>
            <span class="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{{ stats.total }} tickets</span>
          </div>
          <div class="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
            <div
              class="bg-emerald-500 h-full transition-all duration-500 rounded-full"
              :style="{ width: ((stats.accepted / stats.total) * 100) + '%' }"
            ></div>
          </div>
          <div class="flex justify-between text-[11px] font-bold text-slate-600 mt-2">
            <span>✅ Acceptés : {{ stats.accepted }}</span>
            <span>❌ Refusés : {{ stats.refused }}</span>
            <span class="text-emerald-600 font-black">{{ ((stats.accepted / stats.total) * 100).toFixed(1) }}%</span>
          </div>
        </div>
      </div>
    </div>

    <!-- TABLEAU DES DONNÉES DÉTAILLÉES -->
    <div v-if="data" class="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      <div class="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <h2 class="text-xs font-bold uppercase tracking-wider text-slate-400">Données Détaillées</h2>
        <span class="text-xs font-bold text-slate-500">{{ data.choices.length }} enregistrement(s)</span>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-slate-50/75 text-[10px] font-bold uppercase text-slate-400 border-b border-slate-100">
            <tr>
              <th class="px-6 py-4">Date & Heure</th>
              <th class="px-6 py-4">Agence</th>
              <th class="px-6 py-4">File</th>
              <th class="px-6 py-4">Ticket</th>
              <th class="px-6 py-4 text-center">Choix</th>
              <th class="px-6 py-4 text-center">Langue</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            <tr v-for="(choice, idx) in data.choices" :key="idx" class="hover:bg-slate-50/60 transition-colors">
              <td class="px-6 py-4">
                <div class="font-bold text-slate-900">
                  {{ new Date(choice.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) }}
                </div>
                <div class="text-[10px] text-slate-400 font-medium tabular-nums">
                  {{ new Date(choice.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) }}
                </div>
              </td>
              <td class="px-6 py-4 font-semibold text-slate-700">{{ choice.agency }}</td>
              <td class="px-6 py-4 font-semibold text-slate-700">{{ choice.queue }}</td>
              <td class="px-6 py-4 font-mono font-bold text-slate-900">{{ choice.ticketNumber }}</td>
              <td class="px-6 py-4 text-center">
                <span
                  :class="choice.choice === 'Accepte Family Pack'
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-red-50 text-red-700 border-red-200'"
                  class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border"
                >
                  <span>{{ choice.choice === 'Accepte Family Pack' ? '✅' : '❌' }}</span>
                  {{ choice.choice === 'Accepte Family Pack' ? 'Oui' : 'Non' }}
                </span>
              </td>
              <td class="px-6 py-4 text-center">
                <span class="inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">
                  {{ choice.language.toUpperCase() }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- EMPTY STATE -->
    <div v-if="!data && !loading" class="bg-white rounded-2xl shadow-sm border border-slate-200 p-16 text-center">
      <div class="w-14 h-14 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mb-4 mx-auto text-xl">
        📊
      </div>
      <h3 class="text-sm font-bold text-slate-800 mb-1">Aucune donnée disponible</h3>
      <p class="text-xs text-slate-400 font-medium">Sélectionnez une période et cliquez sur "Charger" pour afficher les statistiques.</p>
    </div>

    <!-- LOADING STATE -->
    <div v-if="loading" class="bg-white rounded-2xl shadow-sm border border-slate-200 p-16 text-center">
      <div class="w-10 h-10 border-3 border-slate-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
      <p class="text-xs font-bold text-slate-600">Chargement des données en cours...</p>
    </div>

  </div>
</template>

<script>
import { toast } from 'vue3-toastify';

export default {
  data() {
    return {
      selectedMonth: '',
      selectedAgencyId: '',
      agencySearchQuery: '',
      isAgencyDropdownOpen: false,
      agencies: [],
      data: null,
      loading: false
    };
  },

  computed: {
    selectedAgencyName() {
      const current = this.agencies.find(a => a.id === this.selectedAgencyId);
      return current ? current.nom : '';
    },
    filteredAgencies() {
      if (!this.agencySearchQuery) return this.agencies;
      const q = this.agencySearchQuery.toLowerCase();
      return this.agencies.filter(a => a.nom.toLowerCase().includes(q));
    }
  },

  mounted() {
    const now = new Date();
    this.selectedMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    this.loadAgencies();
  },

  methods: {
    selectAgency(id) {
      this.selectedAgencyId = id;
      this.isAgencyDropdownOpen = false;
      this.agencySearchQuery = '';
    },

    async loadAgencies() {
      try {
        const token = localStorage.getItem('superAdminToken');
        const response = await fetch(
          `https://api.datafuse.fr/api/reports/family-pack-agencies`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (response.ok) {
          const resData = await response.json();
          this.agencies = resData.map(a => ({ id: a._id, nom: a.name }));
        }
      } catch (error) {
        console.error('Erreur chargement agences:', error);
      }
    },

    async loadData() {
      if (!this.selectedMonth) {
        toast.error('Veuillez sélectionner un mois');
        return;
      }

      this.loading = true;
      this.data = null;

      try {
        const token = localStorage.getItem('superAdminToken');
        const url = new URL(`https://api.datafuse.fr/api/reports/family-pack-data`);
        url.searchParams.append('month', this.selectedMonth);
        if (this.selectedAgencyId) {
          url.searchParams.append('agencyId', this.selectedAgencyId);
        }

        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          this.data = await response.json();
          toast.success('Données chargées avec succès');
        } else {
          toast.error('Erreur lors du chargement des données');
        }
      } catch (error) {
        console.error('Erreur chargement données:', error);
        toast.error('Erreur de connexion');
      } finally {
        this.loading = false;
      }
    },

    async exportExcel() {
      if (!this.selectedMonth) {
        toast.error('Veuillez sélectionner un mois');
        return;
      }

      try {
        const token = localStorage.getItem('superAdminToken');
        const url = new URL(`https://api.datafuse.fr/api/reports/family-pack-export`);
        url.searchParams.append('month', this.selectedMonth);
        if (this.selectedAgencyId) {
          url.searchParams.append('agencyId', this.selectedAgencyId);
        }

        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const blob = await response.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = `rapport-family-pack-${this.selectedMonth}${this.selectedAgencyId ? `-${this.selectedAgencyId}` : ''}.xlsx`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(downloadUrl);
          toast.success('Export Excel téléchargé');
        } else {
          toast.error('Erreur lors de l\'export Excel');
        }
      } catch (error) {
        console.error('Erreur export Excel:', error);
        toast.error('Erreur lors de l\'export');
      }
    },

    async exportCSV() {
      if (!this.selectedMonth) {
        toast.error('Veuillez sélectionner un mois');
        return;
      }

      try {
        const token = localStorage.getItem('superAdminToken');
        const url = new URL(`https://api.datafuse.fr/api/reports/family-pack-export-csv`);
        url.searchParams.append('month', this.selectedMonth);
        if (this.selectedAgencyId) {
          url.searchParams.append('agencyId', this.selectedAgencyId);
        }

        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const blob = await response.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = `rapport-family-pack-${this.selectedMonth}${this.selectedAgencyId ? `-${this.selectedAgencyId}` : ''}.csv`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(downloadUrl);
          toast.success('Export CSV téléchargé');
        } else {
          toast.error('Erreur lors de l\'export CSV');
        }
      } catch (error) {
        console.error('Erreur export CSV:', error);
        toast.error('Erreur lors de l\'export');
      }
    }
  }
};
</script>

<style scoped>
/* Scrollbar personnalisée */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>